import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Alert, LTSSService, FinancialSummary, StaffMetrics, ClientUtilization } from '../types/ltss';
import { formatCurrency } from './timeUtils';

export interface PDFReportOptions {
  includeAlerts: boolean;
  includeStaff: boolean;
  includeClients: boolean;
  includeFinancials: boolean;
  includeSummary: boolean;
}

export interface PDFReportData {
  fileName: string;
  services: LTSSService[];
  alerts: Alert[];
  alertSummary: { critical: number; warning: number; caution: number; info: number; total: number };
  staffMetrics: StaffMetrics[];
  clientUtilization: ClientUtilization[];
  financials: FinancialSummary;
}

const COLORS = {
  primary: [59, 130, 246] as [number, number, number],    // Blue
  critical: [239, 68, 68] as [number, number, number],    // Red
  warning: [245, 158, 11] as [number, number, number],    // Yellow/Amber
  caution: [249, 115, 22] as [number, number, number],    // Orange
  info: [59, 130, 246] as [number, number, number],       // Blue
  success: [34, 197, 94] as [number, number, number],     // Green
  dark: [30, 41, 59] as [number, number, number],         // Slate-800
  light: [241, 245, 249] as [number, number, number],     // Slate-100
};

export function generatePDFReport(data: PDFReportData, options: PDFReportOptions): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;

  // Helper to add new page if needed
  const checkPageBreak = (requiredSpace: number = 40) => {
    if (yPos + requiredSpace > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      yPos = 20;
    }
  };

  // Header
  doc.setFillColor(...COLORS.dark);
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('LTSS Analytics Report', 14, 18);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);
  doc.text(`Source: ${data.fileName}`, pageWidth - 14, 28, { align: 'right' });

  yPos = 45;

  // Executive Summary
  if (options.includeSummary) {
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Executive Summary', 14, yPos);
    yPos += 10;

    const summaryData = [
      ['Total Services', data.services.length.toLocaleString()],
      ['Total Revenue', formatCurrency(data.financials.totalAmountPaid)],
      ['Active Staff', data.staffMetrics.length.toString()],
      ['Active Clients', data.clientUtilization.length.toString()],
      ['Total Alerts', data.alertSummary.total.toString()],
      ['Critical Alerts', data.alertSummary.critical.toString()],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [],
      body: summaryData,
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 50 },
      },
      margin: { left: 14 },
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // Alerts Section
  if (options.includeAlerts && data.alerts.length > 0) {
    checkPageBreak(60);
    
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Compliance Alerts', 14, yPos);
    yPos += 5;

    // Alert summary boxes
    const boxWidth = 40;
    const boxHeight = 20;
    const startX = 14;
    
    const alertTypes = [
      { label: 'Critical', count: data.alertSummary.critical, color: COLORS.critical },
      { label: 'Warning', count: data.alertSummary.warning, color: COLORS.warning },
      { label: 'Caution', count: data.alertSummary.caution, color: COLORS.caution },
      { label: 'Info', count: data.alertSummary.info, color: COLORS.info },
    ];

    alertTypes.forEach((type, i) => {
      const x = startX + (i * (boxWidth + 5));
      doc.setFillColor(...type.color);
      doc.roundedRect(x, yPos, boxWidth, boxHeight, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(type.count.toString(), x + boxWidth / 2, yPos + 10, { align: 'center' });
      doc.setFontSize(8);
      doc.text(type.label, x + boxWidth / 2, yPos + 16, { align: 'center' });
    });

    yPos += boxHeight + 10;

    // Alert details table
    const criticalAlerts = data.alerts
      .filter(a => a.type === 'CRITICAL' || a.type === 'WARNING')
      .slice(0, 15);

    if (criticalAlerts.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Level', 'Category', 'Message', 'Staff/Client']],
        body: criticalAlerts.map(alert => [
          alert.type,
          alert.category,
          alert.message.substring(0, 60) + (alert.message.length > 60 ? '...' : ''),
          alert.staffName || alert.clientName || '-',
        ]),
        theme: 'striped',
        headStyles: { fillColor: COLORS.dark, fontSize: 9 },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 30 },
          2: { cellWidth: 90 },
          3: { cellWidth: 35 },
        },
        margin: { left: 14, right: 14 },
        didParseCell: (data) => {
          if (data.column.index === 0 && data.section === 'body') {
            const level = data.cell.raw as string;
            if (level === 'CRITICAL') {
              data.cell.styles.textColor = COLORS.critical;
              data.cell.styles.fontStyle = 'bold';
            } else if (level === 'WARNING') {
              data.cell.styles.textColor = COLORS.warning;
              data.cell.styles.fontStyle = 'bold';
            }
          }
        },
      });

      yPos = (doc as any).lastAutoTable.finalY + 15;
    }
  }

  // Staff Performance Section
  if (options.includeStaff && data.staffMetrics.length > 0) {
    checkPageBreak(60);
    
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Staff Performance', 14, yPos);
    yPos += 8;

    // Sort by manual corrections (highest first)
    const topStaff = [...data.staffMetrics]
      .sort((a, b) => b.manualCorrections - a.manualCorrections)
      .slice(0, 15);

    autoTable(doc, {
      startY: yPos,
      head: [['Staff Name', 'Services', 'Hours', 'Revenue', 'Penalty Pts', 'Clean Rate']],
      body: topStaff.map(staff => [
        staff.staffName,
        staff.totalServices.toString(),
        staff.totalHours.toFixed(1),
        '-',
        staff.manualCorrections.toString(),
        `${staff.cleanRate}%`,
      ]),
      theme: 'striped',
      headStyles: { fillColor: COLORS.dark, fontSize: 9 },
      styles: { fontSize: 8, cellPadding: 2 },
      margin: { left: 14, right: 14 },
      didParseCell: (data) => {
        if (data.column.index === 4 && data.section === 'body') {
          const corrections = parseInt(data.cell.raw as string);
          if (corrections >= 4) {
            data.cell.styles.textColor = COLORS.critical;
            data.cell.styles.fontStyle = 'bold';
          } else if (corrections >= 3) {
            data.cell.styles.textColor = COLORS.warning;
          }
        }
      },
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // Client Utilization Section
  if (options.includeClients && data.clientUtilization.length > 0) {
    checkPageBreak(60);
    
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Client Utilization', 14, yPos);
    yPos += 8;

    // Sort by utilization (lowest first to highlight under-utilization)
    const clientData = [...data.clientUtilization]
      .sort((a, b) => a.utilizationRate - b.utilizationRate)
      .slice(0, 15);

    autoTable(doc, {
      startY: yPos,
      head: [['Client', 'Program', 'Auth Hours', 'Used Hours', 'Utilization', 'Revenue']],
      body: clientData.map(client => [
        client.clientName,
        client.program,
        client.authorizedHours.toFixed(1),
        client.usedHours.toFixed(1),
        `${client.utilizationRate}%`,
        formatCurrency(client.revenue),
      ]),
      theme: 'striped',
      headStyles: { fillColor: COLORS.dark, fontSize: 9 },
      styles: { fontSize: 8, cellPadding: 2 },
      margin: { left: 14, right: 14 },
      didParseCell: (data) => {
        if (data.column.index === 4 && data.section === 'body') {
          const pct = parseInt(data.cell.raw as string);
          if (pct < 50) {
            data.cell.styles.textColor = COLORS.critical;
          } else if (pct < 75) {
            data.cell.styles.textColor = COLORS.warning;
          } else {
            data.cell.styles.textColor = COLORS.success;
          }
        }
      },
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // Financial Summary Section
  if (options.includeFinancials) {
    checkPageBreak(80);
    
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Financial Summary', 14, yPos);
    yPos += 8;

    const financialData = [
      ['Total Units Billed', data.financials.totalUnitsBilled.toLocaleString()],
      ['Total Amount Paid', formatCurrency(data.financials.totalAmountPaid)],
      ['Claims Paid', data.financials.claimsPaid.toString()],
      ['Claims Pending', data.financials.claimsPending.toString()],
      ['Estimated Leakage', formatCurrency(data.financials.estimatedLeakage)],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [],
      body: financialData,
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 4 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60 },
        1: { cellWidth: 50, halign: 'right' },
      },
      margin: { left: 14 },
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;

    // Claims breakdown
    const claimsData = [
      ['Paid', data.financials.claimsPaid.toString(), formatCurrency(data.financials.totalAmountPaid)],
      ['Pending', data.financials.claimsPending.toString(), '-'],
      ['Not Submitted', data.financials.claimsNotSubmitted.toString(), '-'],
      ['Voided', data.financials.claimsVoided.toString(), '-'],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Status', 'Count', 'Amount']],
      body: claimsData,
      theme: 'striped',
      headStyles: { fillColor: COLORS.dark, fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 30, halign: 'center' },
        2: { cellWidth: 50, halign: 'right' },
      },
      margin: { left: 14 },
    });
  }

  // Footer on all pages
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${pageCount} | LTSS Analytics Dashboard | Confidential`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  // Save the PDF
  const timestamp = new Date().toISOString().split('T')[0];
  doc.save(`LTSS-Report-${timestamp}.pdf`);
}
